import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useEffect, useState } from "react";
import { Analytics, AnalyticsBrowser } from "@segment/analytics-next";
import PlausibleProvider from "next-plausible";

import EnvironmentSwitcher from "../components/EnvironmentSwitcher";
import { StoreProvider } from "../src/store";
import useTenant from "../src/useTenant";

import "../styles/globals.css";
import { useOptionalUser, useUserLoggedIn } from "@/src/useUser";
import { sendDataLayerEvent } from "@/src/dataLayer";

export const SegmentContext = React.createContext<Analytics | undefined>(
  undefined
);

export const OnboardingContext = React.createContext<boolean>(false);

const AnalyticsComponent: React.FC<{ analytics?: Analytics }> = ({
  analytics,
}) => {
  const { tenant } = useTenant();
  const { asPath } = useRouter(); // path *and* query string

  useEffect(() => {
    if (analytics) {
      const groupId = tenant?.id || analytics.group().id();
      analytics.page(
        "UI",
        undefined,
        { tenantId: groupId },
        { context: { app: { name: "ui" }, groupId } }
      );
    }
  }, [analytics, asPath, tenant]);

  return <></>;
};

const AnonymousAnalyticsComponent: React.FC<{ analytics?: Analytics }> = ({
  analytics,
}) => {
  const { asPath } = useRouter(); // path *and* query string
  useEffect(() => {
    if (analytics) {
      const groupId = analytics.group().id();
      analytics.page(
        "UI",
        undefined,
        { tenantId: groupId },
        { context: { app: { name: "ui" }, groupId } }
      );
    }
  }, [analytics, asPath]);

  return <></>;
};

const DataLayerComponent: React.FC = () => {
  const { user, userLoggedIn } = useOptionalUser();

  useEffect(() => {
    if (!user) return;

    const payload = {
      provider: user.id.includes("|") ? user.id.split("|")[0] : "unknown",
      userId: user.email,
      name: user.name,
    };

    // Assume user has just signed up if account created w/in last 5 min.
    const justSignedUp =
      parseInt(user.created_at) * 1000 > Date.now() - 5 * 60 * 1000;

    // Log signups and logins.
    if (justSignedUp) {
      sendDataLayerEvent("Signup-TryOso", payload);
    } else {
      sendDataLayerEvent("Login-TryOso", payload);
    }
  }, [userLoggedIn]);

  return null;
};

function MyApp({ Component, pageProps }: AppProps) {
  const [analytics, setAnalytics] = useState<Analytics | undefined>(undefined);
  const [userLoggedIn] = useUserLoggedIn(false);

  // Very few pages require no authentication at all, but it's annoying to try fetch
  // the current user and the current tenant again and again on those pages.
  // If you need to do something that requires the current user or the current tenant or
  // whatever, embed it in a component to avoid violating the laws of hooks, and only
  // include it if isPublicPage is false.
  const isPublicPage =
    (Component as { isPublic?: boolean })["isPublic"] || false;

  const writeKey = process.env.NEXT_PUBLIC_SEGMENT_API_KEY;

  useEffect(() => {
    const loadAnalytics = async () => {
      if (writeKey !== undefined) {
        let [response] = await AnalyticsBrowser.load({
          writeKey,
          cdnURL: "https://sgmt-cdn.analytics.osohq.com",
        });
        setAnalytics(response);
      }
    };
    loadAnalytics();
  }, [writeKey]);

  return (
    <>
      {/* Google Tag Manager / Analytics */}
      {!userLoggedIn && (
        <Script
          id="new-gtm-tag"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            // if (window.location.hostname.endsWith("osohq.com")) {
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-MK29QFG');
            // }
          `,
          }}
        />
      )}
      <Script src="https://3hng953xtt7g.statuspage.io/embed/script.js" />
      <Head>
        <title>Oso Cloud</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <PlausibleProvider
        domain="osohq.com,ui.osohq.com"
        trackOutboundLinks={true}
      >
        <SegmentContext.Provider value={analytics}>
          <StoreProvider>
            {isPublicPage ? (
              <AnonymousAnalyticsComponent analytics={analytics} />
            ) : (
              <>
                <EnvironmentSwitcher></EnvironmentSwitcher>
                <AnalyticsComponent analytics={analytics} />
                {/* NOTE(@uncommoncense): We do not disable this when the user
                 * is logged in because it is used to set the data layer
                 * events post-login. The GTM tag remains immediately
                 * post-login because it is inserted directly into the DOM.
                 * We don't want to remove the `DataLayerComponent` before it
                 * has pushed the data layer events. If the data layer does
                 * not exist, `sendDataLayerEvent` is a no-op.
                 */}
                <DataLayerComponent />
              </>
            )}
            <Component {...pageProps} />
          </StoreProvider>
        </SegmentContext.Provider>
      </PlausibleProvider>
    </>
  );
}

export default MyApp;
